import Image from 'next/image'
import Link from 'next/link'
import { accountUrl } from '@/lib/config'
import { DemoVideoGrid } from '@/components/DemoVideoGrid'
import { TestimonialCarousel } from '@/components/TestimonialCarousel'

const Arrow = () => <span aria-hidden="true">→</span>

const personalOccasions = [
  { label: 'Birthdays', href: '/personal/occasions/birthday' },
  { label: 'Weddings', href: '/personal/occasions/wedding' },
  { label: 'Graduations', href: '/personal/occasions/graduation' },
  { label: 'Anniversaries', href: '/personal/occasions/anniversary' },
  { label: 'Baby showers', href: '/personal/occasions/baby-shower' },
  { label: 'Engagements', href: '/personal/occasions/engagement' },
]

const businessOccasions = [
  { label: 'Farewells', href: '/business/occasions/company-milestone' },
  { label: 'Promotions', href: '/business/occasions/team-achievement' },
  { label: 'Retirements', href: '/business/occasions/company-milestone' },
  { label: 'Work anniversaries', href: '/business/occasions/work-anniversary' },
  { label: 'Client appreciation', href: '/business/occasions/company-milestone' },
  { label: 'Appreciation', href: '/business/occasions/team-achievement' },
]

const faqs = [
  {
    question: 'What is VibeVite?',
    answer:
      'VibeVite is a celebration platform for personal events and workplace moments. Create invitations, collect responses and memories, or build a collaborative recognition wall.',
  },
  {
    question: 'What can guests contribute?',
    answer:
      'Depending on the experience and plan, guests can RSVP and share written messages, photos, video wishes, audio, GIFs, and more.',
  },
  {
    question: 'How are Business Walls different?',
    answer:
      'Business Walls are designed for employee recognition and team celebrations, with collaborative posts, organization branding, administrative controls, and workplace-focused templates.',
  },
  {
    question: 'Do contributors need an account?',
    answer:
      'Hosts can share a simple event or wall link, making it easy for invited guests and colleagues to participate.',
  },
]

const showcaseCards = [
  {
    label: 'Personal Events',
    title: 'Your celebrations, beautifully organized',
    text: 'Manage every event, invitation, RSVP, and shared memory from one simple place.',
    outcome: 'Every RSVP tracked automatically — no spreadsheets needed.',
    href: '',
    hrefText: '',
    image: '/images/home/product-screenshots/personal-event-hub-20260903.png',
    imageAlt:
      'VibeVite Personal Events dashboard showing existing events and the option to create a new event',
    imageWidth: 1356,
    imageHeight: 551,
    bgClass: 'bg-[#F3E3FB]',
  },
  {
    label: 'Personal Templates',
    title: 'Start with a design made for the moment',
    text: 'Choose an occasion, select a beautiful template, and make the experience yours.',
    outcome: '20+ occasion-ready designs, from birthdays to baby showers.',
    href: '',
    hrefText: '',
    image: '/images/home/product-screenshots/personal-event-templates-20260903.png',
    imageAlt: 'VibeVite Personal Events template gallery with occasion filters and invitation designs',
    imageWidth: 1353,
    imageHeight: 548,
    bgClass: 'bg-[#FFF3D6]',
  },
  {
    label: 'Business Walls',
    title: 'Recognition that stays visible and meaningful',
    text: 'Create, manage, and follow every employee milestone and team celebration.',
    outcome: 'Private by default — only invited contributors can post.',
    href: '',
    hrefText: '',
    image: '/images/home/product-screenshots/business-wall-hub-20260903.png',
    imageAlt: 'VibeVite Business Walls dashboard showing live and delivered employee recognition walls',
    imageWidth: 1345,
    imageHeight: 525,
    bgClass: 'bg-[#E3F7DB]',
  },
  {
    label: 'Business Templates',
    title: 'Build the right wall in minutes',
    text: 'Begin with a ready-made wall for appreciation, milestones, farewells, and client events.',
    outcome: 'Live in minutes — no design work required.',
    href: '',
    hrefText: '',
    image: '/images/home/product-screenshots/business-wall-templates-20260903.png',
    imageAlt: 'VibeVite Business Walls template gallery for employee celebrations and achievements',
    imageWidth: 1366,
    imageHeight: 416,
    bgClass: 'bg-[#E1EEFF]',
  },
]

const personalShowcaseCards = showcaseCards.filter((card) => card.label.startsWith('Personal'))
const businessShowcaseCards = showcaseCards.filter((card) => card.label.startsWith('Business'))

const personalDemoVideos = [
  {
    label: 'Guided setup',
    title: 'Build your event page step by step',
    text: 'A guided flow covers the occasion, hosts, experience, location, and date — so you never start from a blank page.',
    thumbBg: 'bg-[#E3F7DB]',
    images: [
      { src: '/images/home/product-screenshots/personal-event-stepper.png', width: 1348, height: 547 },
    ],
  },
  {
    label: 'Invitations & RSVP',
    title: 'Invite guests and track every RSVP',
    text: 'Send email invites, share a link via WhatsApp or SMS, generate a QR code, and manage your guest list and RSVPs in one place.',
    thumbBg: 'bg-[#E1EEFF]',
    images: [
      { src: '/images/home/product-screenshots/personal-event-settings-1.png', width: 764, height: 465 },
    ],
  },
]

const demoVideos = [
  {
    label: 'Birthday',
    title: 'Celebrate birthdays with everyone included',
    text: 'Show the finished birthday wall experience, from shared wishes to a joyful celebration moment.',
    src: '/images/business-demo-videos/optimized/HAPPYBIRTHDAY.mp4',
    poster: '/images/business-demo-videos/posters/HAPPYBIRTHDAY.jpg',
  },
  {
    label: 'Promotion',
    title: 'Recognize big career moments',
    text: 'Highlight a promotion with messages from colleagues in a polished, shareable wall.',
    src: '/images/business-demo-videos/optimized/PROMOTION.mp4',
    poster: '/images/business-demo-videos/posters/PROMOTION.jpg',
  },
]

export function HomeHeroSection() {
  return (
    <section className="mx-auto grid w-full max-w-[1440px] gap-10 px-5 pb-10 pt-10 md:px-10 md:pt-14 lg:grid-cols-[1.08fr_0.92fr] lg:gap-12 lg:px-16">
      <div className="lg:pt-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">
          One platform. Every meaningful moment.
        </p>
        <h1 className="mt-4 max-w-[40rem] text-balance text-5xl font-bold leading-[0.98] tracking-[-0.055em] text-[#1B0044] sm:text-6xl lg:text-7xl">
          Invitations, video wishes, and memories — all in one place to live.
        </h1>
        <span
          className="mt-5 block h-[22px] w-[240px] rounded-[100%] border-t-4 border-[#FF8A3D]"
          aria-hidden="true"
        />
        <p className="mt-5 max-w-[36rem] text-lg leading-relaxed text-[#1B0044]/80">
          Send beautiful invitations, track RSVPs, collect video wishes, and gather shared photos — for the people you celebrate and the teams you lead.
        </p>
        <p className="mt-5 text-lg font-bold text-[#5C177B]">Turn Moments into Memories.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <article className="overflow-hidden rounded-3xl border border-[#E9DDCE] bg-white shadow-[0_18px_45px_rgba(43,10,74,0.12)] transition-transform hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(43,10,74,0.18)]">
          <div className="relative h-56 bg-gradient-to-b from-transparent to-[rgba(27,0,68,0.58)] sm:h-64">
            <Image
              src="/images/home/hero-personal-celebration.webp"
              alt="Family celebrating a birthday together"
              fill
              sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
              className="object-cover"
              priority
            />
          </div>
          <div className="flex min-h-[220px] flex-col p-5">
            <h2 className="text-xl font-bold tracking-[-0.03em]">Personal Events</h2>
            <p className="mt-2 text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#2B0A4A]/50">
              For friends and families
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[#6C6170]">
              Invitations, RSVP, guest wishes, shared photos, live moments, and keepsakes.
            </p>
            <p className="mt-2 text-xs font-semibold text-[#2B0A4A]/50">
              20+ occasions, from birthdays to baby showers
            </p>
            <Link
              href="/personal"
              className="mt-auto inline-flex min-h-11 items-center justify-between rounded-xl bg-[#2B0A4A] px-4 text-xs font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-[#1B0044]"
            >
              Explore Personal Events <Arrow />
            </Link>
          </div>
        </article>

        <article className="overflow-hidden rounded-3xl border border-[#E9DDCE] bg-white shadow-[0_18px_45px_rgba(43,10,74,0.12)] transition-transform hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(43,10,74,0.18)]">
          <div className="relative h-56 bg-gradient-to-b from-transparent to-[rgba(27,0,68,0.68)] sm:h-64">
            <Image
              src="/images/home/hero-business-celebration.webp"
              alt="Colleagues celebrating an employee achievement"
              fill
              sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
              className="object-cover"
            />
          </div>
          <div className="flex min-h-[220px] flex-col p-5">
            <h2 className="text-xl font-bold tracking-[-0.03em]">Business Walls</h2>
            <p className="mt-2 text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#2B0A4A]/50">
              For teams and organizations
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[#6C6170]">
              Collaborative recognition for milestones, achievements, and team culture.
            </p>
            <p className="mt-2 text-xs font-semibold text-[#2B0A4A]/50">
              Private by default, no app required for teams
            </p>
            <Link
              href="/business"
              className="mt-auto inline-flex min-h-11 items-center justify-between rounded-xl bg-[#2B0A4A] px-4 text-xs font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-[#1B0044]"
            >
              Explore Business Walls <Arrow />
            </Link>
          </div>
        </article>
      </div>
    </section>
  )
}

export function HomeTrustBarSection() {
  return (
    <section className="mx-auto mb-16 grid w-full max-w-[1280px] grid-cols-1 rounded-2xl border border-[#E9DDCE] bg-white px-2 py-3 shadow-[0_12px_35px_rgba(69,31,38,0.05)] sm:grid-cols-2 lg:grid-cols-4 lg:px-4">
      {[
        ['One simple link', 'Invite, respond, and contribute'],
        ['No guest app', 'Easy participation from any device'],
        ['Private by default', 'Only invited guests and contributors see it'],
        ['20+ occasions', 'From birthdays to team milestones'],
      ].map(([title, subtitle], idx) => (
        <p
          key={title}
          className={`flex flex-col gap-1 px-5 py-4 ${idx > 0 ? 'border-t border-[#E9DDCE] sm:border-t-0 lg:border-l' : ''}`}
        >
          <strong className="text-xl tracking-[-0.02em] text-[#1B0044]">{title}</strong>
          <span className="text-xs leading-relaxed text-[#766B79]">{subtitle}</span>
        </p>
      ))}
    </section>
  )
}

export function HomeHowItWorksSection() {
  return (
    <section id="how-it-works" className="bg-white px-5 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">How it works</p>
        <h2 className="mt-3 text-balance text-4xl font-bold leading-tight tracking-[-0.04em] text-[#1B0044] sm:text-5xl">
          Create it once. Let everyone add to the story.
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-[#685F70]">
          From setup to keepsake, VibeVite makes participation simple for hosts, guests, teams, and contributors.
        </p>
      </div>

      <div className="mx-auto mt-10 grid max-w-6xl gap-5 md:grid-cols-2 xl:grid-cols-4">
        {[
          {
            step: '01',
            title: 'Choose your experience',
            desc: 'Start with a Personal Event or a Business Wall for your occasion.',
          },
          {
            step: '02',
            title: 'Make it yours',
            desc: 'Choose a design, add the details, and personalize the experience.',
            example: 'A birthday invite, or a recognition wall for a retirement.',
          },
          {
            step: '03',
            title: 'Invite everyone in',
            desc: 'Share one simple link so guests or colleagues can respond and contribute.',
            example: 'Guests for a wedding, or colleagues for a work anniversary.',
          },
          {
            step: '04',
            title: 'Keep the memories',
            desc: 'Enjoy every message, photo, video, and meaningful moment together.',
            example: 'A keepsake video montage, or a lasting wall of appreciation.',
          },
        ].map(({ step, title, desc, example }) => (
          <article key={step} className="border-t-2 border-[#E9DFD6] p-6">
            <span className="text-xs font-extrabold text-[#FF8A3D]">{step}</span>
            <h3 className="mt-3 text-lg font-semibold text-[#1B0044]">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#716776]">{desc}</p>
            {example && (
              <p className="mt-2 text-xs italic leading-relaxed text-[#1B0044]/45">{example}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}

export function HomeOccasionsSection() {
  return (
    <section id="occasions" className="mx-5 my-14 grid gap-7 rounded-[32px] bg-gradient-to-r from-[#F3E8FF] to-[#FFF2D3] p-7 md:mx-12 md:p-12 lg:mx-auto lg:max-w-[1260px] lg:grid-cols-[0.78fr_1.22fr]">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">One home for every milestone</p>
        <h2 className="mt-3 text-balance text-4xl font-bold leading-tight tracking-[-0.04em] text-[#1B0044] sm:text-5xl">
          Big days. Small wins. All worth celebrating.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-[#685F70]">
          VibeVite gives every occasion a thoughtful place where people can show up even when they cannot be there in person.
        </p>
      </div>

      <div className="grid gap-4">
        <div className="rounded-2xl border border-[#2B0A4A]/10 bg-white/60 p-5">
          <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-[#1B0044]">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-white">🎉</span>
            Personal
          </h3>
          <div className="flex flex-wrap gap-2" aria-label="Personal occasions">
            {personalOccasions.map((item, index) => (
              <Link
                key={item.label}
                href={item.href}
                className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80 ${[
                  'bg-white border-[#E5D6CC] text-[#1B0044]',
                  'bg-[#FFC933] border-[#FFC933] text-[#1B0044]',
                  'bg-[#2B0A4A] border-[#2B0A4A] text-white',
                  'bg-[#FFE2D5] border-[#FFE2D5] text-[#1B0044]',
                ][index % 4]}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[#2B0A4A]/10 bg-white/60 p-5">
          <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-[#1B0044]">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-white">🏢</span>
            Business
          </h3>
          <div className="flex flex-wrap gap-2" aria-label="Business occasions">
            {businessOccasions.map((item, index) => (
              <Link
                key={item.label}
                href={item.href}
                className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80 ${[
                  'bg-[#2B0A4A] border-[#2B0A4A] text-white',
                  'bg-[#FFE2D5] border-[#FFE2D5] text-[#1B0044]',
                  'bg-white border-[#E5D6CC] text-[#1B0044]',
                  'bg-[#FFC933] border-[#FFC933] text-[#1B0044]',
                ][index % 4]}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export function HomeShowcaseSection() {
  return (
    <section className="bg-white px-5 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">See VibeVite in action</p>
        <h2 className="mt-3 text-balance text-4xl font-bold leading-tight tracking-[-0.04em] text-[#1B0044] sm:text-5xl">
          One beautiful place for every contribution.
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-[#685F70]">
          From the first invitation to the final wall of memories, every experience feels connected and effortless.
        </p>
      </div>

      <div className="mx-auto mt-12 max-w-[1320px]">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#FF8A3D]">Personal</p>
            <h3 className="mt-2 text-3xl font-bold tracking-[-0.04em] text-[#1B0044]">Plan events and preserve memories.</h3>
          </div>
          <Link href="/personal" className="hidden text-sm font-semibold underline underline-offset-4 hover:text-[#2B0A4A] sm:inline-flex">
            Explore Personal Events <Arrow />
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {personalShowcaseCards.map((card) => (
            <article
              key={card.title}
              className="overflow-hidden rounded-3xl border border-[#E8E2E9] bg-white shadow-[0_12px_32px_rgba(43,10,74,0.07)] transition-transform hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(43,10,74,0.12)]"
            >
              <div className={`overflow-hidden pl-6 pt-6 md:pl-8 md:pt-8 ${card.bgClass}`}>
                <Image
                  src={card.image}
                  alt={card.imageAlt}
                  width={card.imageWidth}
                  height={card.imageHeight}
                  className="h-auto w-full"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-8 pt-6">
                <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#376F4E]">{card.label}</p>
                <h3 className="text-2xl font-bold leading-tight tracking-[-0.035em] text-[#21302B]">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#46534F]">{card.text}</p>
                <p className="mt-3 text-xs font-semibold text-[#2B0A4A]/60">{card.outcome}</p>
                {card.href && card.hrefText ? (
                  <Link
                    href={card.href}
                    className="ml-auto mt-5 flex w-fit items-center gap-2 text-sm underline underline-offset-4 hover:text-[#2B0A4A]"
                  >
                    {card.hrefText} <Arrow />
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <DemoVideoGrid videos={personalDemoVideos} />
      </div>

      <div className="mx-auto mt-20 max-w-[1320px] md:mt-24">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#FF8A3D]">Business</p>
            <h3 className="mt-2 text-3xl font-bold tracking-[-0.04em] text-[#1B0044]">Create recognition walls people want to revisit.</h3>
          </div>
          <Link href="/business" className="hidden text-sm font-semibold underline underline-offset-4 hover:text-[#2B0A4A] sm:inline-flex">
            Explore Business Walls <Arrow />
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {businessShowcaseCards.map((card) => (
            <article
              key={card.title}
              className="overflow-hidden rounded-3xl border border-[#E8E2E9] bg-white shadow-[0_12px_32px_rgba(43,10,74,0.07)] transition-transform hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(43,10,74,0.12)]"
            >
              <div className={`overflow-hidden pl-6 pt-6 md:pl-8 md:pt-8 ${card.bgClass}`}>
                <Image
                  src={card.image}
                  alt={card.imageAlt}
                  width={card.imageWidth}
                  height={card.imageHeight}
                  className="h-auto w-full"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-8 pt-6">
                <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#376F4E]">{card.label}</p>
                <h3 className="text-2xl font-bold leading-tight tracking-[-0.035em] text-[#21302B]">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#46534F]">{card.text}</p>
                <p className="mt-3 text-xs font-semibold text-[#2B0A4A]/60">{card.outcome}</p>
                {card.href && card.hrefText ? (
                  <Link
                    href={card.href}
                    className="ml-auto mt-5 flex w-fit items-center gap-2 text-sm underline underline-offset-4 hover:text-[#2B0A4A]"
                  >
                    {card.hrefText} <Arrow />
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <DemoVideoGrid videos={demoVideos} />
      </div>
    </section>
  )
}

export function PersonalVibeViteInActionSection() {
  return (
    <section className="bg-white px-5 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">VibeVite in action</p>
        <h2 className="mt-3 text-balance text-4xl font-bold leading-tight tracking-[-0.04em] text-[#1B0044] sm:text-5xl">
          See how personal events come together.
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-[#685F70]">
          From planning your event to collecting wishes and memories, VibeVite keeps every personal celebration organized in one place.
        </p>
      </div>

      <div className="mx-auto mt-12 max-w-[1320px]">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#FF8A3D]">Personal Events</p>
            <h3 className="mt-2 text-3xl font-bold tracking-[-0.04em] text-[#1B0044]">Plan events and preserve memories.</h3>
          </div>
          <Link href="/pricing#personal-pricing" className="hidden text-sm font-semibold underline underline-offset-4 hover:text-[#2B0A4A] sm:inline-flex">
            View Personal Pricing <Arrow />
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {personalShowcaseCards.map((card) => (
            <article
              key={card.title}
              className="overflow-hidden rounded-3xl border border-[#E8E2E9] bg-white shadow-[0_12px_32px_rgba(43,10,74,0.07)] transition-transform hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(43,10,74,0.12)]"
            >
              <div className={`overflow-hidden pl-6 pt-6 md:pl-8 md:pt-8 ${card.bgClass}`}>
                <Image
                  src={card.image}
                  alt={card.imageAlt}
                  width={card.imageWidth}
                  height={card.imageHeight}
                  className="h-auto w-full"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-8 pt-6">
                <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#376F4E]">{card.label}</p>
                <h3 className="text-2xl font-bold leading-tight tracking-[-0.035em] text-[#21302B]">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#46534F]">{card.text}</p>
                <p className="mt-3 text-xs font-semibold text-[#2B0A4A]/60">{card.outcome}</p>
                <Link
                  href={card.href}
                  className="ml-auto mt-5 flex w-fit items-center gap-2 text-sm underline underline-offset-4 hover:text-[#2B0A4A]"
                >
                  {card.hrefText} <Arrow />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <DemoVideoGrid videos={personalDemoVideos} />
      </div>
    </section>
  )
}

export function BusinessVibeViteInActionSection() {
  return (
    <section className="bg-white px-5 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">VibeVite in action</p>
        <h2 className="mt-3 text-balance text-4xl font-bold leading-tight tracking-[-0.04em] text-[#1B0044] sm:text-5xl">
          See how Business Walls come together.
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-[#685F70]">
          From choosing a wall template to sharing the finished celebration, VibeWall keeps workplace recognition easy to launch and meaningful to revisit.
        </p>
      </div>

      <div className="mx-auto mt-12 max-w-[1320px]">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#FF8A3D]">Business Walls</p>
            <h3 className="mt-2 text-3xl font-bold tracking-[-0.04em] text-[#1B0044]">Create recognition walls people want to revisit.</h3>
          </div>
          <Link href="/pricing#business-pricing" className="hidden text-sm font-semibold underline underline-offset-4 hover:text-[#2B0A4A] sm:inline-flex">
            View Business Pricing <Arrow />
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {businessShowcaseCards.map((card) => (
            <article
              key={card.title}
              className="overflow-hidden rounded-3xl border border-[#E8E2E9] bg-white shadow-[0_12px_32px_rgba(43,10,74,0.07)] transition-transform hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(43,10,74,0.12)]"
            >
              <div className={`overflow-hidden pl-6 pt-6 md:pl-8 md:pt-8 ${card.bgClass}`}>
                <Image
                  src={card.image}
                  alt={card.imageAlt}
                  width={card.imageWidth}
                  height={card.imageHeight}
                  className="h-auto w-full"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-8 pt-6">
                <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#376F4E]">{card.label}</p>
                <h3 className="text-2xl font-bold leading-tight tracking-[-0.035em] text-[#21302B]">{card.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#46534F]">{card.text}</p>
                <p className="mt-3 text-xs font-semibold text-[#2B0A4A]/60">{card.outcome}</p>
                {card.href && card.hrefText ? (
                  <Link
                    href={card.href}
                    className="ml-auto mt-5 flex w-fit items-center gap-2 text-sm underline underline-offset-4 hover:text-[#2B0A4A]"
                  >
                    {card.hrefText} <Arrow />
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <DemoVideoGrid videos={demoVideos} />
      </div>
    </section>
  )
}

const personalBenefits = [
  'RSVPs tracked automatically, so you always know your headcount.',
  'Video wishes and guest photos collected in one shared place.',
  'Every memory compiled into a keepsake you can revisit for years.',
]

const businessBenefits = [
  'Private by default — only invited contributors can post.',
  'Branded walls for milestones, farewells, and appreciation.',
  'No app required, so every colleague and client can join in.',
]

const personalTestimonials = [
  {
    quote:
      'VibeVite made my daughter’s birthday unforgettable. The heartfelt video wishes gave us a beautiful keepsake we’ll treasure forever.',
    category: 'Personal Events',
    name: 'Priya Sharma',
    detail: 'Austin',
  },
  {
    quote:
      'Our wedding guests recorded messages from around the world. Watching them together made our celebration even more emotional and special.',
    category: 'Personal Events',
    name: 'Daniel Carter',
    detail: 'Seattle',
  },
  {
    quote:
      'We surprised my parents with video wishes for their anniversary. Their reactions were priceless, and the entire process was wonderfully simple.',
    category: 'Personal Events',
    name: 'Sofia Martinez',
    detail: 'Miami',
  },
  {
    quote:
      'The graduation messages from family and friends made my son feel celebrated, even by loved ones who couldn’t attend in person.',
    category: 'Personal Events',
    name: 'Rachel Bennett',
    detail: 'Denver',
  },
]

const businessTestimonials = [
  {
    quote:
      'We collected video messages from employees and clients for our company anniversary. VibeVite brought our journey to life in a personal way.',
    category: 'Business Walls',
    name: 'Arumugam Senthil',
    detail: 'Infovenz Software Solutions',
  },
  {
    quote:
      'VibeVite helped make our product launch more memorable. Gathering customer and team videos was easy, organized, and engaging.',
    category: 'Business Walls',
    name: 'Ranjith Kumar',
    detail: 'Tapceipt IT Solutions Pvt. Ltd.',
  },
]

export function HomeTestimonialsSection() {
  return (
    <section className="bg-[linear-gradient(120deg,#fff1bd,#ffe29b)] px-5 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Built for every kind of celebration</p>
        <h2 className="mt-3 text-balance text-4xl font-bold leading-tight tracking-[-0.04em] text-[#1B0044] sm:text-5xl">
          Meaningful for hosts. Memorable for teams.
        </h2>
      </div>

      <div className="mx-auto mt-10 grid max-w-[1180px] gap-6 lg:grid-cols-2">
        <article className="m-0 rounded-3xl border border-[#2B0A4A]/10 bg-white p-8 shadow-[0_15px_35px_rgba(73,45,16,0.07)]">
          <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#FF8A3D]">Personal Events</span>
          <h3 className="mt-4 text-2xl font-bold tracking-[-0.03em] text-[#1B0044]">Why hosts choose VibeVite</h3>
          <ul className="mt-5 space-y-3">
            {personalBenefits.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-[#1B0044]/85">
                <span className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FFC933]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>

        <article className="m-0 rounded-3xl border border-[#2B0A4A]/10 bg-white p-8 shadow-[0_15px_35px_rgba(73,45,16,0.07)]">
          <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#FF8A3D]">Business Walls</span>
          <h3 className="mt-4 text-2xl font-bold tracking-[-0.03em] text-[#1B0044]">Why teams choose VibeVite</h3>
          <ul className="mt-5 space-y-3">
            {businessBenefits.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-[#1B0044]/85">
                <span className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FFC933]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>
      </div>

      <div className="mx-auto mt-10 max-w-[1180px]">
        <TestimonialCarousel items={[...personalTestimonials, ...businessTestimonials]} />
      </div>
    </section>
  )
}

export function HomeFaqSection() {
  return (
    <section id="faq" className="mx-auto grid max-w-[1200px] gap-8 px-5 py-16 md:px-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16 lg:px-20">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#7A3C83]">Questions, answered</p>
        <h2 className="mt-3 text-balance text-4xl font-bold leading-tight tracking-[-0.04em] text-[#1B0044] sm:text-5xl">
          Everything you need to get the celebration started.
        </h2>
      </div>
      <div className="space-y-0">
        {faqs.map(({ question, answer }) => (
          <details key={question} className="border-t border-[#D8CBBB] py-5 last:border-b">
            <summary className="cursor-pointer list-none text-lg font-semibold text-[#1B0044] marker:content-none">
              <span className="flex items-center justify-between gap-4">
                {question}
                <span className="text-2xl text-[#FF8A3D]" aria-hidden="true">+</span>
              </span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-[#6A606F]">{answer}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

export function HomeFinalCtaSection() {
  return (
    <section className="relative mx-3 mb-12 overflow-hidden rounded-[34px] bg-[radial-gradient(circle_at_18%_18%,rgba(130,72,166,0.92)_0%,rgba(78,34,124,0.88)_28%,rgba(43,10,74,0.98)_62%,#1b0044_100%)] px-5 py-14 text-center text-white md:mx-8 md:px-8 md:py-20 lg:mx-14">
      <span className="absolute left-[12%] top-[22%] text-4xl text-[#FFC933]" aria-hidden="true">✦</span>
      <span className="absolute bottom-[20%] right-[13%] text-2xl text-[#FFC933]" aria-hidden="true">✦</span>
      <div className="relative">
        <p className="mx-auto inline-flex rounded-[2px] bg-[#FFC933] px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#1B0044]">
          Your next celebration starts here
        </p>
        <h2 className="mx-auto mt-4 max-w-5xl text-balance text-4xl font-bold leading-tight tracking-[-0.045em] text-white sm:text-5xl md:text-6xl">
          Make the moment easier to share and impossible to forget.
        </h2>
      </div>
      <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
        <a
          href={`${accountUrl}/app/auth/register`}
          className="inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl bg-[#FFC933] px-6 text-sm font-semibold text-[#1B0044] transition hover:-translate-y-0.5 hover:bg-[#FFD95A] hover:shadow-[0_12px_24px_rgba(0,0,0,0.18)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFC933]"
        >
          Create Your Event <Arrow />
        </a>
        <a
          href={`${accountUrl}/app/business/signup`}
          className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-white/50 bg-transparent px-6 text-sm font-semibold text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFC933]"
        >
          Start Your Wall
        </a>
      </div>
    </section>
  )
}

export function PersonalLandingSections() {
  return (
    <>
      <section className="bg-gradient-to-b from-[#2B0A4A] to-[#1B0044] py-20 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <span className="inline-block rounded-full bg-[#FFC933]/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#FFC933]">
            VibeVite Personal
          </span>
          <h1 className="mt-5 text-3xl font-bold leading-tight sm:text-4xl md:text-5xl">
            The party planning app that does the annoying parts for you.
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-white/85 md:text-lg">
            Invites, RSVPs, video wishes, and guest photos all in one link. You just show up and enjoy it.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={`${accountUrl}/app/auth/register`}
              className="rounded-xl bg-[#FFC933] px-8 py-3.5 font-semibold text-[#2B0A4A] transition-colors hover:bg-[#FF8A3D]"
            >
              Plan My Event Free
            </a>
            <Link
              href="/personal/pricing"
              className="rounded-xl border-2 border-white/50 px-8 py-3.5 font-semibold text-white transition-colors hover:bg-white/10"
            >
              View Personal Pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#FFF8EC] py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-2xl font-bold text-[#2B0A4A] md:text-3xl">
            Why hosts switch to VibeVite
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {[
              ['Stop chasing RSVPs.', 'Automatic reminders do it for you.'],
              ['Skip the group-text video chaos.', 'Everyone records their wish in one spot.'],
              ['Never lose a photo again.', 'Guests upload straight from their phone, no download, no AirDrop.'],
              ['Surprise them.', 'Compile every video wish into one keepsake montage they will watch for years.'],
            ].map(([title, text]) => (
              <div key={title} className="rounded-2xl border border-[#2B0A4A]/10 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-bold text-[#2B0A4A]">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#1B0044]/70 sm:text-base">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-2xl font-bold text-[#2B0A4A] md:text-3xl">
            Popular personal occasions
          </h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[
              ['Birthday', '/birthday-video'],
              ['Wedding', '/wedding-video'],
              ['Baby Shower', '/baby-shower-video'],
              ['Anniversary', '/anniversary-video'],
              ['Graduation', '/graduation-video'],
              ['Engagement', '/engagement-video'],
              ['Gender Reveal', '/gender-reveal-video'],
              ['Valentine\'s Day', '/valentines-day-video'],
              ['Get Well', '/get-well-video'],
              ['Farewell', '/farewell-video'],
              ['Memorial', '/memorial-video'],
              ['Holiday', '/holiday-video'],
            ].map(([label, href]) => (
              <Link
                key={label}
                href={href}
                className="rounded-xl border border-gray-200 bg-[#FFF8EC] p-5 text-[#2B0A4A] shadow-sm transition-colors hover:border-[#FFC933] hover:bg-white"
              >
                <p className="font-semibold">{label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-[#2B0A4A] via-[#1B0044] to-[#2B0A4A] py-16 text-center text-white md:py-24">
        <div className="mx-auto max-w-2xl px-6">
          <h2 className="text-2xl font-bold sm:text-3xl md:text-4xl">
            Start planning. It&apos;s free to try.
          </h2>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={`${accountUrl}/app/auth/register`}
              className="rounded-xl bg-[#FFC933] px-8 py-3.5 font-semibold text-[#2B0A4A] transition-colors hover:bg-white"
            >
              Start Planning
            </a>
            <Link
              href="/personal/pricing"
              className="rounded-xl border-2 border-white/50 px-8 py-3.5 font-semibold text-white transition-colors hover:bg-white/10"
            >
              See Personal Pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
